import clsx from 'clsx';
import { useMemo } from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ChevronDownIcon, CircleDollarSignIcon } from 'lucide-react';
import { CalendarIcon } from 'lucide-react';

import ErrorIcon from '@/assets/icons/exclamation-circle.svg';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { POOL } from '@/types/channels';
import { formatShortDate } from '@/utils/date-util';
import { Tag } from '@/components/ui/tag';
import DocumentTextIcon from '@/assets/icons/document-text.svg';
import { getTotalChannelBudget } from '../utils/channel-utils';

interface ChannelSummaryProps {
  channelKey: string;
  setIsExpanded?: (isExpanded: boolean) => void;
}

export function ChannelSummary({
  channelKey,
  setIsExpanded,
}: ChannelSummaryProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { hookForm } = useJobPostingContext();
  const { setValue } = hookForm;
  const { errors } = useFormState({ control: hookForm.control });

  const hasErrors = errors?.channels?.[channelKey];

  const channelData = useWatch({
    control: hookForm.control,
    name: `channels.${channelKey}`,
  });
  const {
    slug: channelSlug,
    name: channelName,
    displayTitle,
    vonqDetails,
    isFree,
  } = channelData;
  const isPool = channelSlug === POOL;
  const isContract = vonqDetails?.contractId != null;
  const { startsAt, endAt, isExpanded } = channelData;
  const totalChannelBudget = getTotalChannelBudget(channelData);

  const toggleExpanded = () => {
    if (setIsExpanded) {
      setIsExpanded(!isExpanded);
      return;
    }

    setValue(`channels.${channelKey}.isExpanded`, !isExpanded, {
      shouldDirty: false,
    });
  };

  const displayBudget = useMemo(() => {
    if (isPool || isFree)
      return intl.formatMessage({
        defaultMessage: 'Free',
        description: 'Free budget',
      });

    if (!totalChannelBudget) return '-';

    return formatCurrency(totalChannelBudget);
  }, [formatCurrency, intl, isPool, isFree, totalChannelBudget]);

  return (
    <button
      type="button"
      onClick={toggleExpanded}
      className={clsx(
        'border-fx-neutral-200 hover:bg-fx-neutral-50 mb-2 w-full rounded-lg border bg-white px-4 py-6 shadow-sm',
        hasErrors && 'border-fx-rose',
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <ChannelIcon
          channelSlug={channelSlug}
          variant="large"
          channelName={channelName}
          displayTitle={displayTitle}
          className="min-w-0 flex-1"
          logoSquareUrl={vonqDetails?.logoSquareUrl}
          logoRectangleUrl={vonqDetails?.logoRectangleUrl}
        />

        <div className="flex shrink-0 items-center gap-2">
          {/* Duration */}
          <Tag
            icon={<CalendarIcon className="text-fx-neutral-600 size-3" />}
            text={`${formatShortDate(startsAt) ?? 'Today'} - ${endAt ? formatShortDate(endAt) : 'Ongoing'}`}
            className="bg-fx-neutral-100"
            textClassName="text-fx-neutral-900"
          />

          {/* Budget */}
          {isContract ? (
            <Tag
              text={intl.formatMessage({
                defaultMessage: 'Contract',
                description: 'Placeholder for contract budget',
              })}
              className="bg-fx-blue-50"
              textClassName="text-fx-blue-800"
              icon={
                <img src={DocumentTextIcon} alt="Contract" className="size-3" />
              }
            />
          ) : (
            <Tag
              icon={
                <CircleDollarSignIcon className="text-fx-neutral-600 size-3" />
              }
              text={displayBudget}
              className="bg-fx-neutral-100"
              textClassName="text-fx-neutral-900"
            />
          )}

          {/* Error */}
          {hasErrors && (
            <Tooltip
              content={intl.formatMessage({
                defaultMessage:
                  'Some fields are incomplete or have invalid entries. Please review and fix the highlighted fields.',
                description: 'Error message for required fields',
              })}
            >
              <img src={ErrorIcon} alt="Error" />
            </Tooltip>
          )}

          <ChevronDownIcon className="text-fx-neutral-400" />
        </div>
      </div>
    </button>
  );
}
